import domReady from '@wordpress/dom-ready';

/**
 * Things We Love — pinned scroll-through.
 *
 * Same division of labour as the destinations block: CSS `position: sticky`
 * (see &__sticky) does the pinning, and this only reads how far the user has
 * scrolled into that pinned span to decide which entry is highlighted. No
 * scroll-hijacking, no preventDefault.
 *
 * It enhances the server-rendered markup in place rather than re-rendering
 * it, so the list is real HTML to anything that never runs this -- with the
 * first entry highlighted, the design's own resting state.
 */

// Matches $ocean-mobile in scss/_thresh.scss. Mobile un-pins the section
// (the list is just read top to bottom), so the height this sets would be
// sizing a section the CSS has already released. Kept in sync by hand.
const MOBILE_QUERY = '(max-width: 782px)';

// How much scrolling each entry gets, as a fraction of the viewport height.
// Deliberately well under the full viewport-per-item the destinations block
// gives its full-bleed tabs: here only a line of type changes weight, so a
// long hold reads as the page having stuck. This is the dial to turn if the
// section wants to feel slower or quicker.
const VH_PER_ITEM = 0.45;

const initSection = (section: HTMLElement) =>
	{
	const items = Array.from(section.querySelectorAll<HTMLElement>('.wp-block-ocean-things-we-love-item'));
	const slides = Array.from(section.querySelectorAll<HTMLElement>('.wp-block-ocean-things-we-love__slide'));

	if (items.length === 0)
		{
		return;
		}

	const query = window.matchMedia(MOBILE_QUERY);
	let activeIndex = -1;

	const setActive = (index: number) =>
		{
		if (index === activeIndex)
			{
			return;
			}
		activeIndex = index;
		items.forEach((item, i) => item.classList.toggle('is-active', i === index));

		// Slides are keyed to their entry's position rather than to their own
		// order in the stack: entries that set no photo of their own are
		// skipped when the stack is built, so the two lists can differ in
		// length and index alignment cannot be assumed.
		//
		// Nothing matching means this entry inherits the photo already
		// showing, which is the fallback render.php sets up -- so leave the
		// stack as it is rather than fading to blank.
		const match = slides.find((slide) => Number(slide.dataset.slide) === index);

		if (match)
			{
			slides.forEach((slide) => slide.classList.toggle('is-active', slide === match));
			}
		};

	const setSectionHeight = () =>
		{
		if (query.matches)
			{
			// Released back to its natural height -- the sticky child is
			// static at this width, so an explicit height here would just
			// leave a gap below the list.
			section.style.height = '';
			return;
			}

		// One viewport for the band to sit in while pinned, plus the distance
		// the entries advance over.
		//
		// Measured against the viewport, not the band's own height: the
		// sticky child is viewport-tall (see &__sticky), so that is what
		// actually gets pinned. Reserving the band's height instead left the
		// child unstuck for the remainder, which showed as a tall empty gap
		// under the section once the last entry had been reached.
		const pinned = (items.length - 1) * VH_PER_ITEM * window.innerHeight;
		section.style.height = `${window.innerHeight + pinned}px`;
		};

	let ticking = false;
	const onScroll = () =>
		{
		if (ticking)
			{
			return;
			}
		ticking = true;
		window.requestAnimationFrame(() =>
			{
			ticking = false;

			if (query.matches)
				{
				// Un-pinned: the list is read in order, so the highlight has
				// nothing to track. First entry stays lit.
				setActive(0);
				return;
				}

			// Measured from where the section pins (its top reaching the
			// viewport top), so the first entry holds until the user has
			// actually scrolled a step into the pinned span rather than
			// advancing the moment the section arrives.
			const step = VH_PER_ITEM * window.innerHeight;
			const sectionTop = section.getBoundingClientRect().top + window.scrollY;
			const scrolled = window.scrollY - sectionTop;
			const index = Math.floor(scrolled / step);
			setActive(Math.min(Math.max(index, 0), items.length - 1));
			});
		};

	setSectionHeight();
	setActive(0);
	onScroll();

	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', () => { setSectionHeight(); onScroll(); });
	query.addEventListener('change', () => { setSectionHeight(); onScroll(); });
	};

domReady(() =>
	{
	document
		.querySelectorAll<HTMLElement>('.wp-block-ocean-things-we-love')
		.forEach(initSection);
	});
